import { maybe } from '@passionware/monads';
import { Callsheet } from '__types__';
import { ApiClient } from 'lib/api/ApiClient.types';
import { scheduledFilter } from 'v5/core/standard-date-filter/scheduled-filter';
import { timestampFilter } from 'v5/core/standard-date-filter/timestamp-filter';
import {
  convertEnumFilterToApi,
  convertEnumFilterToArrayApi
} from 'v5/platform/filters/filterConverters';
import { z } from 'zod';
import { WithWorkspace } from '../_common/query/workspace';
import { CallsheetApi, CallsheetSearchQuery } from './callsheet.api';
import {
  convertCreateCallsheetInputToRequest,
  CreateCallsheetInput
} from './callsheet.api.http.converter';

/**
 * Convert v5 filters to API format
 * API expects: { field_name: { $operator: value } } or { field_name: [values] }
 * Per API documentation: https://docs...
 */
function convertFiltersToApi(
  filters: CallsheetSearchQuery['filters']
): Record<string, unknown> {
  const apiFilters: Record<string, unknown> = {};

  if (!filters) {
    return apiFilters;
  }

  // Status filter (BUILDING/SHARE) - API supports $eq, $in, or array format
  if (maybe.isPresent(filters.status)) {
    const statusFilter = convertEnumFilterToApi(filters.status, value => value);
    if (statusFilter) {
      apiFilters.status = statusFilter;
    }
  }

  // From date filter - uses callsheet_date field with open ranges for timestamp
  if (maybe.isPresent(filters.callsheetDate)) {
    apiFilters.callsheet_date = scheduledFilter.createHttpConverter()(
      filters.callsheetDate
    );
  }

  // Archived filter - handled at top level, not in filters
  // (per API documentation, archived should be top-level parameter)

  // User ID filter (Owner) - API supports $eq or array format
  if (maybe.isPresent(filters.userId)) {
    const userFilter = convertEnumFilterToApi(filters.userId, value => value);
    if (userFilter) {
      apiFilters.user_id = userFilter;
    }
  }

  if (maybe.isPresent(filters.productionIds)) {
    const productionFilter = convertEnumFilterToArrayApi(
      filters.productionIds,
      value => value
    );
    if (productionFilter) {
      apiFilters.production_ids = productionFilter;
    }
  }

  if (maybe.isPresent(filters.createdAt)) {
    apiFilters.created_at =
      timestampFilter.createHttpConverter()(filters.createdAt) ?? null;
  }

  return apiFilters;
}

/**
 * Convert v5 sorter to API format
 * API expects: { field: string, direction: 'ASC' | 'DESC' }
 */
function convertSorterToApi(sorter: CallsheetSearchQuery['sort']): {
  field: string;
  direction: 'ASC' | 'DESC';
} {
  if (!sorter) {
    return { field: 'created_at', direction: 'DESC' };
  }

  const fieldMap: Record<string, string> = {
    createdAt: 'created_at',
    updatedAt: 'updated_at',
    title: 'title',
    fromDate: 'from_date',
    status: 'status'
  };

  return {
    field: fieldMap[sorter.field] || 'created_at',
    direction: sorter.order.toUpperCase() as 'ASC' | 'DESC'
  };
}

async function searchCallsheets(
  client: ApiClient,
  query: WithWorkspace<CallsheetSearchQuery>
): Promise<{ items: Callsheet[]; count: number }> {
  const apiQuery: Record<string, unknown> = {
    // Text search query
    ...(query.search && query.search.trim().length > 0
      ? { query: query.search.trim() }
      : {}),

    // Pagination (API uses 1-based page numbers)
    p: query.page ? query.page.page + 1 : 1,
    count: query.page ? query.page.pageSize : 50,

    // Archived filter (top-level parameter per API documentation)
    archived: query.filters?.archived?.value ?? false,

    // Filters object
    filters: convertFiltersToApi(query.filters),

    // Sorting
    ...(query.sort ? { order_by: convertSorterToApi(query.sort) } : {})
  };

  console.log('[Callsheet API] Making request to /search/callsheets', {
    apiQuery,
    workspace: query.workspace
  });

  const response = await client.post(`/search/callsheets`, {
    data: apiQuery,
    token: true
  });

  const parsed = z
    .object({
      results: z.array(z.any()), // Using z.any() since Callsheet type is complex
      paging: z.object({
        total: z.number(),
        page: z.number(),
        count_per_page: z.number()
      })
    })
    .parse(response);

  return {
    items: parsed.results as Callsheet[],
    count: parsed.paging.total
  };
}

async function getCallsheet(
  client: ApiClient,
  callsheetId: number,
  _workspace: string
): Promise<Callsheet | null> {
  try {
    const response = await client.get<Callsheet>(`/callsheets/${callsheetId}`, {
      token: true
    });
    return response;
  } catch (error) {
    console.error('Failed to fetch callsheet:', error);
    return null;
  }
}

async function createCallsheet(
  client: ApiClient,
  workspace: string,
  input: CreateCallsheetInput
): Promise<Callsheet> {
  const request = convertCreateCallsheetInputToRequest(input);
  const response = await client.post<Callsheet>('/callsheets', {
    data: request,
    token: true
  });
  return response;
}

async function deleteCallsheet(
  client: ApiClient,
  callsheetId: number,
  _workspace: string
): Promise<void> {
  await client.put<Callsheet>(`/callsheets/${callsheetId}`, {
    data: { archived: true },
    token: true
  });
}

async function unarchiveCallsheet(
  client: ApiClient,
  callsheetId: number,
  _workspace: string
): Promise<Callsheet> {
  const response = await client.put<Callsheet>(`/callsheets/${callsheetId}`, {
    data: { archived: false },
    token: true
  });
  return response;
}

/**
 * Create HTTP-based callsheet API implementation
 */
export function createCallsheetApiHttp(client: ApiClient): CallsheetApi {
  return {
    searchCallsheets: async (query: WithWorkspace<CallsheetSearchQuery>) => {
      return searchCallsheets(client, query);
    },

    getCallsheet: async (callsheetId: number, workspace: string) => {
      return getCallsheet(client, callsheetId, workspace);
    },

    createCallsheet: async (workspace: string, input: CreateCallsheetInput) => {
      return createCallsheet(client, workspace, input);
    },

    deleteCallsheet: async (callsheetId: number, workspace: string) => {
      return deleteCallsheet(client, callsheetId, workspace);
    },

    unarchiveCallsheet: async (callsheetId: number, workspace: string) => {
      return unarchiveCallsheet(client, callsheetId, workspace);
    }
  };
}
